import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Filters from "components/collection/filters";
import CollectionNav from "components/collection/nav";
import clsx from "clsx/lite";
import { ReactComponent as CategoriesIcon } from "assets/svgs/categories.svg";
import HeaderBag from "components/header-bag";
import Search from "components/search-products";
import CartProgress from "components/cart/progress";
import { ReactComponent as Menu } from "assets/svgs/menu.svg";
import { shortDate } from "helpers/format-date";
import obtainOrderInfo from "utils/obtain-order-info";
import { useGlobalContext } from "context/global";
import { allMeals, cartMeals } from "utils/constants";
import { setView } from "slices/global";

const Views = () => {
	const { translation, isSubscriptionPage } = useGlobalContext();
	const { nextBillingDate, chargeOffsetDays, reactivationFlow, deliveryDate } = useSelector(state => state.account.subscription);
	const { latestSubOrder } = useSelector((state) => state.account.profile);
	const { date } = obtainOrderInfo(nextBillingDate, chargeOffsetDays, latestSubOrder, deliveryDate);
	const { view } = useSelector(state => state.global);
	const dispatch = useDispatch();

	const inReactivationFlow = [2, 3].includes(reactivationFlow.step);

	const myMealsLabel = isSubscriptionPage
		? translation.subscription?.my_meals + (nextBillingDate && !inReactivationFlow ? `: ${shortDate(date)}` : "")
		: translation.collection?.my_selection;

	const allMealsLabel = inReactivationFlow ? translation.subscription?.choose_your_meals : translation.subscription?.all_meals;

	return (
		<div className="flex flex-wrap mb-2 lg:mb-0 lg:gap-3 gap-1">
			<button
				onClick={() => dispatch(setView(allMeals))}
				className={clsx("items-center lg:items-start justify-center rounded-full px-3 mr-2",
					"py-0.5 lg:mr-0 lg:p-3 text-[11px] lg:text-base cursor-pointer flex relative",
					view === allMeals ? "bg-light-orange" : "bg-white-smoke hover:bg-dark-gray")}>
				<Menu className="fill-black" />
				<span className="ml-2">
					{allMealsLabel}
				</span>
			</button>

			<button
				onClick={() => dispatch(setView(cartMeals))}
				className={clsx("items-center justify-center rounded-full px-3 py-1 lg:p-3",
					"text-[11px] lg:text-base cursor-pointer flex relative",
					view === cartMeals ? "bg-light-orange" : "bg-white-smoke hover:bg-dark-gray")}>
				{myMealsLabel}
			</button>
		</div>
	);
};

const CollectionHeader = ({ collections, filters }) => {
	const { translation, isSubscriptionPage } = useGlobalContext();
	const { totalWeight, activeBox, subscriptionId, reactivationFlow } = useSelector(state => state.account.subscription);
	const { isAuthenticated } = useSelector((state) => state.account.profile);
	const { isSearchOpen } = useSelector((state) => state.filters);
	const { view } = useSelector(state => state.global);
	const { totalProgress } = useSelector(state => state.cart);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [view]);

	return (
		<div className={clsx("hd-screen-max-width sticky top-0 lg:top-18 block",
			reactivationFlow.isActive && "lg:!top-0",
			"bg-white z-[41] lg:z-40 lg:border-b lg:border-black lg:px-6 lg:pb-6 pt-4",
			isSubscriptionPage ? "lg:pt-2" : "lg:pt-14 xl:pt-2"
		)}>
			<div className="flex justify-between pl-3 md:pr-3 lg:pr-0 lg:pl-0 border-b border-platinum border-solid lg:border-none">
				{!isSearchOpen && <div className="flex gap-3 items-center">
					<div className="group hidden lg:block">
						{collections && <button disabled={view === cartMeals}
							className={clsx("items-center bg-white-smoke justify-center rounded-full p-3 hidden lg:flex relative",
								view === cartMeals ? "opacity-30 cursor-none pointer-events-none" :
									"cursor-pointer group-hover:bg-light-orange hover:bg-light-orange")}>
							<CategoriesIcon className="stroke-black" />
							<span className="ml-2">{translation.subscription?.categories}</span>
						</button>}
						<div className={clsx("left-0 absolute w-1/4 z-[41] opacity-0 transition-all duration-500 ease-in-out",
							"pointer-events-none shadow-lg bg-white pl-10 pr-4 pt-10 pb-1 rounded-[1.25rem]",
							view !== cartMeals && "group-hover:opacity-100 group-hover:pointer-events-auto")}>
							{collections && <CollectionNav collections={collections} />}
						</div>
					</div>
					<div className="block lg:hidden mb-0.5">
						{collections && <CollectionNav collections={collections} hideCollections />}
					</div>
					{filters && <Filters filters={filters} hideResults />}
					<div className="h-10 w-px bg-platinum lg:bg-black self-center lg:mx-2" />
					<Views />
				</div>}
				<div className={clsx("flex gap-2 items-end mb-3 lg:mb-0", isSearchOpen && "w-full")}>
					<Search />
					<div className="hidden lg:flex gap-2 items-end">
						<CartProgress totalProgress={subscriptionId && isSubscriptionPage ? totalWeight : totalProgress}
							box={isSubscriptionPage && activeBox} className="!mt-0 mb-1 w-52 flex-shrink-0 ml-8" />
						<div className={clsx("relative", isAuthenticated ? "ml-2" : "ml-3")}><HeaderBag /></div>
					</div>
				</div>
			</div>
			{filters && <div className="hidden lg:block"><Filters filters={filters} hideResults={false} hideTitle /></div>}
			{collections && <div className="block lg:hidden">
				<CollectionNav collections={collections} hideCollections={false} hideFilter />
			</div>}
		</div>
	);
};

export default CollectionHeader;